import { usePersistedBlotterTable } from '@talos/kyoko';
import { useFeatureFlag } from '../../../../hooks';
import { defaultSubAccountsBlotterColumns, defaultSubAccountsBlotterFilter } from '../defaults';
import type { SubAccountPositionsTabProps } from '../types';
import { usePositionsSubAccountsColumns } from './usePositionsSubAccountsColumns';
import { usePositionsSubAccountsFilter } from './usePositionsSubAccountsFilter';

export interface UsePositionsSubAccountsBlotterFoundationParams {
  blotterID: string;
  tab: SubAccountPositionsTabProps;
}

/**
 * Shared persisted layout, filter builder, and column setup for desktop and mobile
 * sub-account positions blotters. Call once per mount before variant-specific wiring.
 */
export function usePositionsSubAccountsBlotterFoundation({
  blotterID,
  tab,
}: UsePositionsSubAccountsBlotterFoundationParams) {
  const { enableHierarchicalSubAccountPositionsBlotter } = useFeatureFlag();

  const defaultColumns = tab.defaultColumns ?? defaultSubAccountsBlotterColumns;
  const defaultFilter = tab.defaultFilter ?? defaultSubAccountsBlotterFilter;
  const showZeroBalances = tab.showZeroBalances ?? false;
  const hierarchical = (tab.hierarchical && enableHierarchicalSubAccountPositionsBlotter) ?? false;
  const includeCash = tab.includeCash ?? true;

  const columns = usePositionsSubAccountsColumns(defaultColumns);
  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter });

  const filterResults = usePositionsSubAccountsFilter({
    persistedBlotterTable,
    defaultFilter,
    hierarchical,
  });

  return {
    columns,
    persistedBlotterTable,
    filterResults,
    hierarchical,
    showZeroBalances,
    includeCash,
    enableHierarchicalSubAccountPositionsBlotter,
  };
}

export type PositionsSubAccountsBlotterFoundation = ReturnType<typeof usePositionsSubAccountsBlotterFoundation>;
